/**
 * Size Guide Drawer Custom Element
 *
 * Follows Dawn's cart-drawer pattern, reusing Dawn's native utilities:
 * - trapFocus() / removeTrapFocus() from global.js
 * - overflow-hidden body class for scroll lock
 *
 * Usage:
 *   <size-guide-drawer class="drawer drawer--size-guide">...</size-guide-drawer>
 *
 * Open programmatically:
 *   document.querySelector('size-guide-drawer').open(triggerElement);
 *
 * Trigger via data attribute:
 *   <button data-size-guide-trigger>Size Guide</button>
 */
class SizeGuideDrawer extends HTMLElement {
  constructor() {
    super();
    this.overlay = this.querySelector('#SizeGuideDrawer-Overlay');
    this.drawerInner = this.querySelector('.drawer__inner');
    this.activeElement = null;

    // ESC key to close
    this.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape') this.close();
    });

    // Overlay click to close
    if (this.overlay) {
      this.overlay.addEventListener('click', this.close.bind(this));
    }

    // Initialize pill navigation
    this.initPills();

    // Bind global triggers
    this.bindTriggers();
  }

  /**
   * Bind click events for all [data-size-guide-trigger] elements
   */
  bindTriggers() {
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-size-guide-trigger]');
      if (trigger) {
        event.preventDefault();
        this.open(trigger);
      }
    });
  }

  /**
   * Open the drawer
   * @param {HTMLElement} triggeredBy - The element that triggered the open
   */
  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    // Add active class after a brief delay to ensure animation triggers
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    // Trap focus after transition completes
    this.addEventListener(
      'transitionend',
      () => {
        const focusElement = this.drawerInner || this.querySelector('.drawer__close');
        if (typeof trapFocus === 'function') {
          trapFocus(this.querySelector('#SizeGuideDrawer'), focusElement);
        }
      },
      { once: true }
    );

    // Lock body scroll
    document.body.classList.add('overflow-hidden');
  }

  /**
   * Close the drawer
   */
  close() {
    this.classList.remove('active');

    // Remove focus trap and restore focus to trigger
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }

    // Unlock body scroll
    document.body.classList.remove('overflow-hidden');
  }

  /**
   * Store the element that triggered the drawer open
   * @param {HTMLElement} element
   */
  setActiveElement(element) {
    this.activeElement = element;
  }

  /**
   * Initialize pill navigation for size tables
   */
  initPills() {
    const pills = this.querySelectorAll('[data-size-pill]');
    if (pills.length === 0) return;

    pills.forEach((pill) => {
      pill.addEventListener('click', () => {
        const targetId = pill.getAttribute('data-size-pill');
        this.switchTable(targetId);
      });

      // Arrow key navigation
      pill.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          const pillsArray = Array.from(pills);
          const currentIndex = pillsArray.indexOf(pill);
          let nextIndex;

          if (e.key === 'ArrowRight') {
            nextIndex = currentIndex < pillsArray.length - 1 ? currentIndex + 1 : 0;
          } else {
            nextIndex = currentIndex > 0 ? currentIndex - 1 : pillsArray.length - 1;
          }

          pillsArray[nextIndex].focus();
          pillsArray[nextIndex].click();
        }
      });
    });
  }

  /**
   * Switch active size table
   * @param {string} targetId - The block ID of the table to show
   */
  switchTable(targetId) {
    // Update pills
    const pills = this.querySelectorAll('[data-size-pill]');
    pills.forEach((pill) => {
      const isTarget = pill.getAttribute('data-size-pill') === targetId;
      pill.classList.toggle('is-active', isTarget);
      pill.setAttribute('aria-selected', isTarget ? 'true' : 'false');
    });

    // Update tables
    const tables = this.querySelectorAll('[data-size-table]');
    tables.forEach((table) => {
      const isTarget = table.getAttribute('data-size-table') === targetId;
      table.classList.toggle('is-active', isTarget);
      table.hidden = !isTarget;
    });
  }
}

customElements.define('size-guide-drawer', SizeGuideDrawer);
